import { MultiSelect } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import * as styles from './AuditItemsFilters.css';
import { createAuditItemFilterOptions } from './helpers';
import type { AuditItemFilters, AuditItemFilterOptions } from './types';

interface FilterGridProps {
  filters: AuditItemFilters;
  filterOptions: AuditItemFilterOptions;
  isLoading: boolean;
  onStatusFilterChange: (selectedValue: string | undefined) => void;
  onLabelFilterChange: (selectedValue: string | undefined) => void;
  onSeverityFilterChange: (selectedValue: number | undefined) => void;
  onMultipleDecisionsStatusFilterChange: (selectedValue: string | undefined) => void;
}

/**
 * Filter grid component containing all filter sections
 * Updated to match API specification - single-value filters for status, label, severity
 * Removed unsupported filters: decisionStatus, languages, topics
 */
export function FilterGrid({
  filters,
  filterOptions,
  isLoading,
  onStatusFilterChange,
  onLabelFilterChange,
  onSeverityFilterChange,
  onMultipleDecisionsStatusFilterChange,
}: FilterGridProps) {
  // Prepare options for Select/MultiSelect components using helper
  const { statusOptions, labelOptions, severityOptions, multipleDecisionsStatusOptions } = useMemo(
    () => createAuditItemFilterOptions(filterOptions),
    [filterOptions]
  );

  return (
    <div className={styles.filterGrid}>
      {/* Status Filter - Single Select */}
      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>Status</div>
        <div className={styles.multiSelectWrapper}>
          <MultiSelect
            disabled={isLoading}
            options={statusOptions}
            placeholder="Select status..."
            selectedValues={filters.status ? [filters.status] : []}
            onSelectionChange={values => onStatusFilterChange(values[0])}
          />
        </div>
      </div>

      {/* Label Filter - Single Select */}
      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>Label</div>
        <div className={styles.multiSelectWrapper}>
          <MultiSelect
            searchable
            disabled={isLoading}
            options={labelOptions}
            placeholder="Select label..."
            searchPlaceholder="Search labels..."
            selectedValues={filters.label ? [filters.label] : []}
            onSelectionChange={values => onLabelFilterChange(values[0])}
          />
        </div>
      </div>

      {/* Severity Filter - Single Select */}
      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>Severity</div>
        <div className={styles.multiSelectWrapper}>
          <MultiSelect
            disabled={isLoading}
            options={severityOptions}
            placeholder="Select severity..."
            selectedValues={filters.severity !== undefined ? [filters.severity.toString()] : []}
            onSelectionChange={values => onSeverityFilterChange(values[0] ? Number(values[0]) : undefined)}
          />
        </div>
      </div>

      {/* Multiple Decisions Status Filter - Single Select */}
      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>Multiple Decisions</div>
        <div className={styles.multiSelectWrapper}>
          <MultiSelect
            disabled={isLoading}
            options={multipleDecisionsStatusOptions}
            placeholder="Select decision status..."
            selectedValues={filters.multipleDecisionsStatus ? [filters.multipleDecisionsStatus] : []}
            onSelectionChange={values => onMultipleDecisionsStatusFilterChange(values[0])}
          />
        </div>
      </div>
    </div>
  );
}
